// src/App.js

import React, { useState, useEffect, useRef, lazy } from 'react';
import { Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useNavigate } from 'react-router-dom';

// ========== AMPLITUDE ANALYTICS ==========
// import * as amplitude from '@amplitude/analytics-browser';
// import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

// ========== CONTEXTS, STYLES, UTILS ==========
import { useAuth } from './contexts/AuthContext';
import useDeviceType from './components/useDeviceType';
import theme from './theme';
import GlobalStyles from './GlobalStyles';
import apiCall from './utils/api';
import AppLoadingHandler from './contexts/AppLoadingHandler';
import { setIOSStatusBarColor, resetIOSStatusBarColor } from './utils/iosStatusBar';
// import { getValidSpecialities, getInvalidSpecialities, specialityOptionsData } from './utils/specialityData';

// ========== LAYOUTS & ROUTE GUARDS ==========
import ProtectedRoute from './utils/ProtectedRoute';
import { speIconMapGénéral } from './utils/domainIconMap';

// =========================================================================
// ============= TOUS LES COMPOSANTS SONT LAZY-LOADÉS PAR DÉFAUT =============
// =========================================================================
// Le pré-chargement est maintenant géré dynamiquement dans le composant App.
// Seul le layout principal de l'application est pré-chargé de manière statique.

// --- Layouts & Wrappers ---
const AppLayout = lazy(() => import(/* webpackPreload: true, webpackChunkName: "app-layout" */ './AppLayout'));
const SocialRoutesWrapper = lazy(() => import('./SocialRoutesWrapper'));
const ProtectedClassementOrQuiz = lazy(() => import('./utils/ProtectedClassementOrQuiz'));

// --- Onboarding & Public Flow ---
const FicheFormation = lazy(() => import('./components/FicheFormation'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const SignUp = lazy(() => import('./components/SignUp'));
const FirstPage = lazy(() => import('./components/FirstPage'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const MeoFirstMessage = lazy(() => import('./components/MeoFirstMessage'));
const Pseudo = lazy(() => import('./components/Onboarding/Pseudo'));
const Choice = lazy(() => import('./components/Onboarding/Choice'));
const UserTypeSelection = lazy(() => import('./components/Onboarding/UserTypeSelection'));
const TypeBac = lazy(() => import('./components/Onboarding/TypeBac'));
const FormationOnboarding = lazy(() => import('./components/Onboarding/FormationOnboarding'));
const ProblèmesFormationOnboarding = lazy(() => import('./components/Onboarding/ProblèmesFormationOnboarding'));
const Lycee = lazy(() => import('./components/Onboarding/Lycee'));
const ClassementClasse = lazy(() => import('./components/Onboarding/ClassementClasse'));
const GeneralMoyenne = lazy(() => import('./components/Onboarding/GeneralMoyenne'));
const DomainSelection = lazy(() => import('./components/Onboarding/DomainSelection'));
const DomainSelectionPrincipaux = lazy(() => import('./components/Onboarding/DomainSelectionPrincipaux'));
const CitySelection = lazy(() => import('./components/Onboarding/CitySelection'));
const PhoneSelection = lazy(() => import('./components/Onboarding/PhoneSelection'));
const PartageDonnées = lazy(() => import('./components/Onboarding/PartageDonnées'));
const BacGeneral = lazy(() => import('./components/Onboarding/BacGeneral'));
const BacTechnologique = lazy(() => import('./components/Onboarding/BacTechnologique'));
const BacProfessionnel = lazy(() => import('./components/Onboarding/BacProfessionnel'));
const YearStudy = lazy(() => import('./components/Onboarding/YearStudy'));
const MessagePret = lazy(() => import('./components/MessagePret'));
const PresentationFeatures = lazy(() => import('./components/PresentationFeatures'));

// --- Authenticated User Components ---
const ChatDiscussion = lazy(() => import('./components/ChatDiscussion'));
// Removed desktop chat in favor of unified mobile chat with desktop tweaks
const UserProfilePage = lazy(() => import('./components/Profile/UserProfilePage'));
const Settings = lazy(() => import('./components/Settings'));
const Favoris = lazy(() => import('./components/Favoris'));
const Outils = lazy(() => import('./components/Outils'));
const Comparateur = lazy(() => import('./components/Comparateur'));
const QuizTypeFormation = lazy(() => import('./components/QuizTypeFormation'));

// --- Lettres Flow ---
const LettresSelectFormation = lazy(() => import('./components/Lettres/SelectFormation'));
const LettresValidateFormation = lazy(() => import('./components/Lettres/ValidateFormation'));
const LettresQuestions = lazy(() => import('./components/Lettres/Questions'));
const LettresWait = lazy(() => import('./components/Lettres/WaitLettre'));
const LettresRelecture = lazy(() => import('./components/Lettres/Relecture'));
const LettresFinal = lazy(() => import('./components/Lettres/Final'));

// --- Other Components ---
const ManagerProfile = lazy(() => import('./components/Manager/Profile'));
const ManagerAnalytics = lazy(() => import('./components/Manager/Analytics'));
const ManagerLeads = lazy(() => import('./components/Manager/Leads'));
const ManagerUpdate = lazy(() => import('./components/Manager/Update'));
const ManagerLandingPage = lazy(() => import('./components/LandingPage/Manager'));
const ManagerLogin = lazy(() => import('./components/LandingPage/ManagerLogin'));
const ModifySwipeAnswers = lazy(() => import('./components/ModifySwipeAnswers'));
const DomainesFavoris = lazy(() => import('./components/Social/DomainesFavoris'));
const UserListPage = lazy(() => import('./components/Social/UserListPage'));
const InstagramConnect = lazy(() => import('./components/InstagramConnect'));
const UsersFeedBack = lazy(() => import('./components/UsersFeedback'));

// --- Ambassador Components ---
const AmbassadorResponsePage = lazy(() => import('./components/AmbassadorResponsePage'));


// Utility to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// ==================================================================
// ========================== APP COMPONENT =========================
// ==================================================================

function App() {
  const { loading: isAuthLoading, isAuthenticated, user, checkAuth } = useAuth();
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if app entry has been incremented this session
  const appEntryIncrementedRef = useRef(false);
  
  // // Track if Amplitude has been initialized to ensure it only happens once
  // const amplitudeInitializedRef = useRef(false);

  // // ========== AMPLITUDE INITIALIZATION ==========
  // useEffect(() => {
  //   // Initialize Amplitude only once and only on client-side
  //   // Skip initialization on localhost/development
  //   if (!amplitudeInitializedRef.current && typeof window !== 'undefined') {
  //     const isLocalhost = window.location.hostname === 'localhost' || 
  //                        window.location.hostname === '127.0.0.1' || 
  //                        window.location.hostname.startsWith('192.168.') ||
  //                        window.location.hostname.startsWith('10.') ||
  //                        window.location.hostname.includes('.local');
      
  //     if (isLocalhost) {
  //       console.log('Amplitude Analytics disabled on localhost/development environment');
  //       amplitudeInitializedRef.current = true; // Mark as initialized to prevent retries
  //       return;
  //     }
      
  //     try {
  //       // Add Session Replay plugin with 100% sample rate
  //       amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));
        
  //       // Initialize Amplitude with autocapture enabled
  //       amplitude.init('1cb77eb8706680b36ed5660c9ab1db1a', { autocapture: true });
        
  //       amplitudeInitializedRef.current = true;
  //       console.log('Amplitude Analytics and Session Replay initialized successfully');
  //     } catch (error) {
  //       console.error('Failed to initialize Amplitude:', error);
  //     }
  //   }
  // }, []); // Empty dependency array ensures this runs only once

  // ==========================================================
  // === NOUVEAU : Stratégie de Prefetching Intelligent ici ===
  // ==========================================================
  useEffect(() => {
    // Ce hook s'exécute uniquement quand `isAuthenticated` change.
    
    if (isAuthenticated) {
      // ===== UTILISATEUR CONNECTÉ =====
      // On pré-charge en arrière-plan les composants de l'application principale.
      console.log('Prefetching authenticated user assets...');
      
      import(/* webpackPrefetch: true, webpackChunkName: "chat-discussion" */ './components/ChatDiscussion');
      import(/* webpackPrefetch: true, webpackChunkName: "user-profile" */ './components/Profile/UserProfilePage');
      import(/* webpackPrefetch: true, webpackChunkName: "settings" */ './components/Settings');
      import(/* webpackPrefetch: true, webpackChunkName: "favoris" */ './components/Favoris');
      import(/* webpackPrefetch: true, webpackChunkName: "outils" */ './components/Outils');
      
    } else {
      // ===== UTILISATEUR DÉCONNECTÉ =====
      // On pré-charge en arrière-plan le début du tunnel d'onboarding.
      console.log('Prefetching onboarding assets...');

      import(/* webpackPrefetch: true, webpackChunkName: "login-page" */ './components/LoginPage');
      import(/* webpackPrefetch: true, webpackChunkName: "signup-page" */ './components/SignUp');
      import(/* webpackPrefetch: true, webpackChunkName: "onboarding-pseudo" */ './components/Onboarding/Pseudo');
    }
    
  }, [isAuthenticated]);

  // ==========================================================
  // === SYSTÈME DE VERSIONNING GLOBAL ===
  // ==========================================================
  useEffect(() => {
    const checkVersion = () => {
      const localVersion = localStorage.getItem('version_app');
      const appVersion = process.env.REACT_APP_VERSION || '0.0.1';

      if (!localVersion || localVersion !== appVersion) {
        console.log(`🔄 Version changed: ${localVersion} → ${appVersion}`);
        console.log('🧹 Clearing ranking caches...');
        
        // Nettoyer UNIQUEMENT les caches de rankings et filtres
        localStorage.setItem('need_update_ranking', 'true');
        localStorage.setItem('need_update_ranking_formation', 'true');
        localStorage.removeItem('ranking_formation');
        localStorage.removeItem('ranking_types');
        
        // Mettre à jour la version
        localStorage.setItem('version_app', appVersion);
        console.log('✅ Version updated');
      }
    };

    checkVersion();
  }, []); // Exécuté une seule fois au montage de l'app

  // --- Global Effects (Loader, Viewport, Theme color, etc.) ---
  useEffect(() => {
    if (!isAuthLoading) {
      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.classList.add('fade-out');
        setTimeout(() => loader.parentNode?.removeChild(loader), 550);
      }
    }
  }, [isAuthLoading]);
  
  useEffect(() => {
    const updateViewportHeight = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    window.addEventListener('resize', updateViewportHeight);
    updateViewportHeight();
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  useEffect(() => {
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      let color = theme.colors.background;
      if (location.pathname.startsWith('/classement')) color = theme.colors.white;
      else if (location.pathname.startsWith('/outils')) color = theme.colors.orange_fonce;
      else if (location.pathname === '/social') color = theme.colors.bleu_fonce;
      themeColorMeta.content = color;
    }
  }, [location.pathname]);

  // --- iOS Status Bar Management (Global fallback) ---
  // Cette gestion globale assure que la status bar est réinitialisée lors des changements de route
  // Les composants spécifiques (/social, /outils) peuvent override cette valeur dans leur propre useEffect
  useEffect(() => {
    // Les pages /social et /outils gèrent leur propre couleur dans leurs composants
    // Pour toutes les autres pages, on réinitialise à blanc
    if (!location.pathname.startsWith('/social') && !location.pathname.startsWith('/outils')) {
      resetIOSStatusBarColor();
    }
    // Note: Les pages /social et /outils définissent leur couleur dans leur propre composant
  }, [location.pathname]);

  // --- User-specific Effects (App Entry, Phone Number check) ---
  useEffect(() => {
    if (isAuthenticated && !appEntryIncrementedRef.current) {
      appEntryIncrementedRef.current = true;
      apiCall(`${process.env.REACT_APP_BACKEND_URL}/auth/increment-app-entry/`, 'POST')
        .then(async data => {
          // Update localStorage for backward compatibility
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const parsed = JSON.parse(storedUser);
            parsed.number_of_app_entries = data.number_of_app_entries;
            // Store streak information from the response
            if (data.current_streak !== undefined) {
              parsed.current_streak = data.current_streak;
              parsed.longest_streak = data.longest_streak;
              parsed.streak_updated_today = data.streak_updated_today;
            }
            localStorage.setItem('user', JSON.stringify(parsed));
          }
          
          // Refresh AuthContext to sync with updated backend data
          await checkAuth();
          console.log('✅ App entry incremented and AuthContext refreshed');
        })
        .catch(err => console.error('Error incrementing app entry', err));
    }
  }, [isAuthenticated, checkAuth]);

  const popular_cities_names = [
      'Paris',
      'Lyon',
      'Lille',
      'Toulouse',
      'Bordeaux',
      'Aix-Marseille',
      'Montpellier',
      'Rennes',
      'Strasbourg',
      'Grenoble',
      'Nantes',
      'Nancy',
      'Rouen',
      'Nice',
      'Angers',
      'Clermont-Ferrand',
      'Dijon',
      'Caen',
      'Amiens',
      'Tours',
      'Reims',
      'Poitiers',
      'Saint-Etienne',
      'Brest',
      'Besançon',
      'Metz',
      'Orléans',
      'Limoges',
      'Valenciennes',
      'Toulon'
  ];
  
  // Helper function to check if specialities are valid
  const areSpecialitiesValid = (userSpecialities) => {
    if (!userSpecialities || !Array.isArray(userSpecialities)) {
      return false;
    }
    
    // Get all valid speciality values (including arts)
    const validSpecialities = [...speIconMapGénéral.map(spec => spec.value)];
    console.log("Valid specialities:", validSpecialities);
    console.log("User specialities:", userSpecialities);
    console.log("Are user specialities valid?", userSpecialities.every(spec => validSpecialities.includes(spec)));

    return userSpecialities.every(spec => validSpecialities.includes(spec));
  };
  
  useEffect(() => {
    if (isAuthenticated && user) {
      // Ne pas faire de redirections si on est sur une route ambassadeur
      if (location.pathname.startsWith('/ambassador/')) {
        return;
      }
      
      const { user_type, phone_number, search_a_school, user_city, specialites } = user;
      
      // Determine if user has completed onboarding (not a new user)
      const hasCompletedOnboarding = user?.number_of_app_entries > 0 || user?.onboarding_completed === true;
      
      // Check specialities validity first for general bac users - BUT ONLY for users who have completed onboarding
      if (hasCompletedOnboarding && user?.type_bac === 'general' && !areSpecialitiesValid(specialites)) {
        console.log('Redirecting to specialities selection due to invalid or missing specialities :', specialites);
        if (!location.pathname.includes('/profile/modify/specialites')) {
          navigate('/profile/modify/specialites');
          return;
        }
      }
      
      // Check for generic location that needs to be updated - BUT ONLY for users who have completed onboarding
      if (hasCompletedOnboarding && !user_city) {
        if (!location.pathname.includes('/profile/modify/city')) {
          navigate('/profile/modify/city');
          return;
        }
      } else if (hasCompletedOnboarding && user_city) {
        // Split by comma, trim, and check if each city is in popular_cities_names
        const cities = user_city.split(',').map(c => c.trim());
        const allCitiesArePopular = cities.every(city =>
          popular_cities_names.includes(city)
        );
        if (
          user_city === 'Toute la France' ||
          user_city === 'Aucune idée' ||
          !allCitiesArePopular
        ) {
          if (!location.pathname.includes('/profile/modify/city')) {
          navigate('/profile/modify/city');
          return;
          }
        }
      }
      
      // Original phone number check - BUT ONLY for users who have completed onboarding
      if (hasCompletedOnboarding && user_type !== 'student' && (!phone_number || phone_number.trim() === "") && (search_a_school === true || search_a_school === undefined)) {
        navigate('/profile/modify/phone');
      }
    }
  }, [isAuthenticated, user, navigate, location.pathname]);

  // Redirect returning users missing user_type_2026 to the selection screen on app access
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Ne pas faire de redirections si on est sur une route ambassadeur
    if (location.pathname.startsWith('/ambassador/')) {
      return;
    }

  // Determine if user is new vs returning
  const entries = Number(user?.number_of_app_entries || 0);
  const isNewUser = true  //entries <= 0; // first time (before we increment) → treat as new

  // Show prompt when user_type_2026 is not set: null, undefined, or string "null" (as sometimes serialized)
  const ut26 = user?.user_type_2026;
  const shouldPrompt = ut26 === null || ut26 === undefined || ut26 === 'null';

  // Only redirect if not new AND needs prompt
  if (!isNewUser && shouldPrompt) {
      // Avoid redirect loops if we're already on an onboarding route OR city selection
      const path = location.pathname;
      const isOnOnboarding = [
        '/user-type-selection',
        '/parent-child-selection',
        '/type-bac',
        '/bac1',
        '/lycee',
        '/profile/modify/user-type',
        '/profile/modify/city',  // Add city selection to prevent conflicts
      ].some(prefix => path.startsWith(prefix));
      if (!isOnOnboarding) {
        // Replace history entry so going back doesn't land on /login or a stale page
        navigate('/profile/modify/user-type?target=2026', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, location.pathname]);

  // =================================================
  // ============ RENDER LOGIC: THE ROUTER ===========
  // =================================================
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <ScrollToTop />
      <Routes>

          {/* ============================= */}
          {/* === REGULAR USER ROUTES === */}
          {/* ============================= */}
          <Route element={ <AppLoadingHandler><Outlet/></AppLoadingHandler> }>

            {/* --- Public & Onboarding Routes --- */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/first-login" element={<SignUp />} />
            
            {/* Onboarding Flow */}
            <Route path="/presentation-meo" element={<MeoFirstMessage />} />
            <Route path="/choice" element={<Choice />} />
            <Route path="/pseudo" element={<Pseudo />} />
            <Route path="/user-type-selection" element={<UserTypeSelection />} />
            <Route path="/parent-child-selection" element={<UserTypeSelection />} />
            <Route path="/type-bac" element={<TypeBac />} />
            <Route path="/bac1" element={<FormationOnboarding />} />
            <Route path="/lycee" element={<Lycee />} />
            <Route path="/problemes-formation" element={<ProblèmesFormationOnboarding />} />
            <Route path="/general-moyenne" element={<GeneralMoyenne />} />
            <Route path="/domaines-favoris" element={<DomainSelection />} />
            <Route path="/domaines-principaux" element={<DomainSelectionPrincipaux />} />
            <Route path="/origine-city" element={<CitySelection />} />
            <Route path="/message-pret" element={<MessagePret />} />
            <Route path="/presentation-features" element={<PresentationFeatures />} />
            <Route path="/phone-selection" element={<PhoneSelection />} />
            <Route path="/autorisation-partage" element={<PartageDonnées />} />
            <Route path="/bac-general" element={<BacGeneral />} />
            <Route path="/bac-technologique" element={<BacTechnologique />} />
            <Route path="/bac-professionnel" element={<BacProfessionnel />} />
            <Route path="/classe-ment" element={<ClassementClasse />} />
            <Route path="/year-study" element={<YearStudy />} />

            {/* --- Public Landing Pages --- */}
            <Route path="/first-page" element={<LandingPage />} />
            
            {/* --- Manager Routes (outside emulator) --- */}
            <Route path="/manager" element={<ManagerLandingPage />} />
            <Route path="/manager/login" element={<ManagerLogin />} />
            <Route path="/manager/dashboard" element={<ManagerProfile />} />
            <Route path="/manager/profile" element={<ManagerProfile />} />
            <Route path="/manager/analytics" element={<ManagerAnalytics />} />
            <Route path="/manager/leads" element={<ManagerLeads />} />
            <Route path="/manager/update" element={<ManagerUpdate />} />

            {/* --- Root Path Handling --- */}
            <Route path="/" element={<RootRedirect />} />

            {/* --- Formation Route (accessible to both authenticated and unauthenticated users) --- */}
            <Route element={<AppLayout />}>
              <Route path="/formation" element={<FicheFormation />} />
            </Route>

            {/* --- Authenticated Routes --- */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                {/* Unified Chat Route: use mobile chat everywhere, pass isMobile to tweak UI */}
                <Route path="/chat" element={<ChatDiscussion isMobile={isMobile} />} />
                <Route path="/profile" element={<UserProfilePage isDesktop={!isMobile} />} />
                <Route path="/profile/:userId" element={<UserProfilePage isDesktop={!isMobile} />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/aide-meo" element={<UsersFeedBack />} />

                {/* SHARED AUTHENTICATED ROUTES */}
                <Route path="/classement" element={<ProtectedClassementOrQuiz isMobile={isMobile} />} />
                <Route path="/quiz" element={<QuizTypeFormation />} />
                <Route path="/formations-favorites" element={<Favoris />} />
                <Route path="/filieres-favorites" element={<Favoris />} />
                <Route path="/comparateur" element={<Comparateur />} />
                <Route path="/outils" element={<Outils />} />
                <Route path="/lettres" element={<LettresSelectFormation />} />
                <Route path="/lettres-validate" element={<LettresValidateFormation />} />
                <Route path="/lettres-questions" element={<LettresQuestions />} />
                <Route path="/lettres-relecture" element={<LettresRelecture />} />
                <Route path="/lettres-wait" element={<LettresWait />} />
                <Route path="/lettres-final" element={<LettresFinal />} />
                <Route path="/social/*" element={<SocialRoutesWrapper />} />
                <Route path="/instagram-connect" element={<InstagramConnect />} />
                <Route path="/profile/:userId/domaines" element={<DomainesFavoris />} />
                <Route path="/profile/:userId/followers" element={<UserListPage />} />
                <Route path="/profile/:userId/following" element={<UserListPage />} />

                {/* PROFILE MODIFY ROUTES */}
                <Route path="/profile/modify/city" element={<CitySelection />} />
                <Route path="/profile/modify/phone" element={<PhoneSelection />} />
                <Route path="/profile/modify/instagram" element={<InstagramConnect isModifyMode={true} />} />
                <Route path="/profile/modify/domaines-favoris" element={<DomainSelection />} />
                <Route path="/profile/modify/domaines-principaux" element={<DomainSelectionPrincipaux />} />
                <Route path="/profile/modify/lycee" element={<Lycee />} />
                <Route path="/profile/modify/classe-ment" element={<ClassementClasse />} />
                <Route path="/profile/modify/formation" element={<FormationOnboarding />} />
                <Route path="/profile/modify/grades" element={<GeneralMoyenne />} />
                <Route path="/profile/modify/specialites" element={user?.type_bac === 'professionnel' ? <BacProfessionnel /> : user?.type_bac === 'technologique' ? <BacTechnologique /> : user?.type_bac === 'general' ? <BacGeneral /> : <TypeBac />} />
                <Route path="/profile/modify/type-bac" element={<TypeBac />} />
                <Route path="/profile/modify/swipe" element={<ModifySwipeAnswers isMobile={isMobile} />} />
                <Route path="/profile/modify/user-type" element={<UserTypeSelection />} />
                <Route path="/profile/modify/pseudo" element={<Pseudo />} />
              </Route>
            </Route>
            
            {/* --- Fallback for any other route --- */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
          
        </Routes>
        
    </ThemeProvider>
  );
}

const RootRedirect = () => {
  const { isAuthenticated, user } = useAuth();
  const { isMobile } = useDeviceType();

  if (isAuthenticated) {
    const redirectTo = user?.user_type === 'student' 
      ? '/social' 
      : '/classement';
    return <Navigate to={redirectTo} replace />;
  }

  // Show LandingPage on desktop, FirstPage on mobile
  return isMobile ? <FirstPage /> : <LandingPage />;
};

export default App;